/**
 * The WAIT of «Войти через Telegram» through the bot — the loop that asks the
 * server whether the person has pressed Start in Telegram, shared by the surface
 * that tapped (`components/telegram-bot-login.tsx`) and the app-wide resumer
 * that collects a confirmation that surface is no longer mounted to collect
 * (`components/telegram-bot-login-resume.tsx`).
 *
 * One loop, so the two can never disagree about cadence, the hidden-page pause
 * or the deadline. WHO may run it for a given request is decided by
 * `claimBotLoginWait` (`telegram-bot-login.ts`): at most one loop per token,
 * because the exchange is single-use.
 */
import { AuthApiError, UnauthorizedError, type TelegramBotLoginStatus } from './auth-client';
import {
  FIRST_POLL_MS,
  POLL_WINDOW_MS,
  RETURN_GRACE_MS,
  nextPollDelayMs,
  type PendingBotLogin,
} from './telegram-bot-login';

/**
 * A server answer that ends the wait — a refusal worth telling the person
 * (409 `telegram_account_taken`, a shell the ledger will not merge, a malformed
 * token). Everything else — a 429, a 5xx, no answer at all — is retried.
 */
export function isBotLoginRefusal(err: unknown): boolean {
  if (err instanceof UnauthorizedError) return true;
  if (!(err instanceof AuthApiError)) return false;
  return err.code !== 'rate_limited' && err.code !== 'internal_error';
}

/** How a wait ended without a session — `bot_login_<this>` in the funnel row. */
export type BotLoginWaitEnd = 'timeout' | 'expired' | 'invalid' | 'consumed';

export type BotLoginSignedIn = Extract<TelegramBotLoginStatus, { status: 'signed_in' }>;

export interface BotLoginWaitHandlers {
  /** THIS poll won the exchange: the server has signed this browser in. */
  signedIn: (st: BotLoginSignedIn) => void;
  /** A refusal (see `isBotLoginRefusal`). */
  refused: (err: unknown) => void;
  /** The request ended without a session. */
  ended: (how: BotLoginWaitEnd) => void;
  /**
   * Asked before every poll. `false` stops the loop silently (no callback):
   * the request is no longer this loop's to collect.
   */
  stillMine?: () => boolean;
}

/**
 * Wait on `pending`: ~2 s polls (backing off, see `nextPollDelayMs`), none
 * while the page is hidden — the person is in Telegram, and an answer could
 * not be acted on — and one the moment it is visible again. Gives up at the
 * request's own expiry (fifteen minutes, `POLL_WINDOW_MS`), except that a
 * person coming back after that still gets `RETURN_GRACE_MS` of asking: a
 * Start pressed just before the expiry stays collectable for fifteen minutes
 * more, and only the server knows whether there was one.
 *
 * At most one handler fires, once; the loop then stops. Returns `cancel`.
 * `cancel({ keepSignedIn: true })` still delivers a `signed_in` from a poll
 * that is already in flight — for a hand-over, where that poll may be the one
 * that won the (single-use) exchange and its answer must not be dropped.
 */
export function runBotLoginWait(
  pending: PendingBotLogin,
  poll: (token: string) => Promise<TelegramBotLoginStatus>,
  handlers: BotLoginWaitHandlers,
  opts: { firstPollMs?: number } = {},
): (how?: { keepSignedIn?: boolean }) => void {
  let cancelled = false;
  let keepSignedIn = false;
  let inFlight = false;
  let failures = 0;
  let timer = 0;
  let deadline = Math.min(pending.expiresAt, pending.startedAt + POLL_WINDOW_MS);

  const schedule = (ms: number) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void tick(), ms);
  };

  const stop = (how: { keepSignedIn?: boolean } = {}) => {
    if (!cancelled) keepSignedIn = how.keepSignedIn === true;
    cancelled = true;
    window.clearTimeout(timer);
    document.removeEventListener('visibilitychange', onReturn);
    window.removeEventListener('focus', onReturn);
    window.removeEventListener('pageshow', onReturn);
  };

  async function tick(): Promise<void> {
    if (cancelled || inFlight) return;
    if (handlers.stillMine && !handlers.stillMine()) {
      stop();
      return;
    }
    // Hidden: the person is in Telegram. Asking now costs a request and
    // cannot be acted on; the visibility handler asks the moment they return.
    if (document.visibilityState === 'hidden') return;
    if (Date.now() > deadline) {
      stop();
      handlers.ended('timeout');
      return;
    }
    inFlight = true;
    let st: TelegramBotLoginStatus;
    try {
      st = await poll(pending.token);
      failures = 0;
    } catch (err) {
      inFlight = false;
      if (cancelled) return;
      if (isBotLoginRefusal(err)) {
        stop();
        handlers.refused(err);
        return;
      }
      failures += 1;
      schedule(nextPollDelayMs(Date.now() - pending.startedAt, failures));
      return;
    }
    inFlight = false;
    if (cancelled) {
      if (keepSignedIn && st.status === 'signed_in') {
        keepSignedIn = false;
        handlers.signedIn(st);
      }
      return;
    }
    if (st.status === 'pending') {
      schedule(nextPollDelayMs(Date.now() - pending.startedAt));
      return;
    }
    stop();
    if (st.status === 'signed_in') handlers.signedIn(st);
    // expired | invalid (not this browser's request — a lost cookie) |
    // consumed (this browser already exchanged it — another tab).
    else handlers.ended(st.status);
  }

  function onReturn(): void {
    if (document.visibilityState !== 'visible') return;
    // Back from Telegram. A person who took longer than the poll window still
    // gets an answer — the server's, which is `expired` if there was no Start.
    if (Date.now() > deadline) deadline = Date.now() + RETURN_GRACE_MS;
    schedule(0);
  }

  document.addEventListener('visibilitychange', onReturn);
  // Some WebViews report the return only as focus, or restore from bfcache.
  window.addEventListener('focus', onReturn);
  window.addEventListener('pageshow', onReturn);
  schedule(opts.firstPollMs ?? FIRST_POLL_MS);
  return stop;
}
